import axios from 'axios';

const trimTrailingSlash = (url) => url?.replace(/\/$/, '');

// Prefer the full API URL (CI/deploy provides this). Fall back to legacy backend URL.
const apiFromEnv = trimTrailingSlash(process.env.REACT_APP_API_URL);
const legacyBackend = trimTrailingSlash(process.env.REACT_APP_BACKEND_URL);
const fallbackRuntimeApi =
  typeof window !== 'undefined' ? `${trimTrailingSlash(window.location.origin)}/api` : 'http://127.0.0.1:8000/api';

const API =
  apiFromEnv ||
  (legacyBackend ? `${legacyBackend}/api` : undefined) ||
  fallbackRuntimeApi;

const ANON_ID_KEY = 'snapgrad_anon_id';

export const getOrCreateAnonymousId = () => {
  if (typeof window === 'undefined') return null;
  try {
    let anonId = window.localStorage.getItem(ANON_ID_KEY);
    if (anonId && typeof anonId === 'string' && anonId.length > 0) {
      return anonId;
    }

    if (window.crypto?.randomUUID) {
      anonId = window.crypto.randomUUID();
    } else {
      anonId = `anon_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
    }

    window.localStorage.setItem(ANON_ID_KEY, anonId);
    return anonId;
  } catch {
    return null;
  }
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Add request interceptor to attach Authorization header
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Get the token from localStorage (set by AuthCallback)
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
      
      if (!clientId) {
        console.warn('[API] REACT_APP_COGNITO_CLIENT_ID not set in environment');
        return config;
      }
      
      const lastAuthResultKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthResult`;
      const authResultStr = localStorage.getItem(lastAuthResultKey);
      
      if (!authResultStr) {
        console.debug('[API] No auth result found in localStorage for key:', lastAuthResultKey);
        console.debug('[API] Available keys:', Object.keys(localStorage));
        return config;
      }
      
      const authResult = JSON.parse(authResultStr);
      
      if (!authResult.accessToken) {
        console.warn('[API] Auth result found but no accessToken:', authResult);
        return config;
      }
      
      config.headers.Authorization = `Bearer ${authResult.accessToken}`;
      console.debug(`[API] ${config.method?.toUpperCase()} ${config.url} - Authorization header set`);
    } catch (error) {
      console.error('[API] Failed to attach Authorization header:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to log responses and errors
apiClient.interceptors.response.use(
  (response) => {
    console.debug(`[API] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url;
    const method = error.config?.method?.toUpperCase();
    
    console.error(`[API] ${status} ${method} ${url}`);
    console.error('[API] Error details:', {
      status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      headers: error.response?.headers,
    });
    
    // Log 401 specifically for auth issues
    if (status === 401) {
      console.warn('[API] 401 Unauthorized - Token may be invalid or expired');
    }
    
    return Promise.reject(error);
  }
);

// API functions
export const schoolAPI = {
  // Search for schools based on location query
  searchSchools: async (query, options = {}, config = {}) => {
    try {
      const payload = {
        query,
        latitude: options.latitude,
        longitude: options.longitude,
        radius_miles: options.radius_miles,
        limit: options.limit,
        offset: options.offset,
      };
      const response = await apiClient.post('/schools/search', payload, config);
      return response.data;
    } catch (error) {
      console.error('Error searching schools:', error);
      throw new Error(error.response?.data?.detail || 'Failed to search schools');
    }
  },

  // Anonymous search endpoint with rate limiting on backend
  searchSchoolsPublic: async (query, options = {}, config = {}) => {
    try {
      const payload = {
        query,
        latitude: options.latitude,
        longitude: options.longitude,
        radius_miles: options.radius_miles,
        limit: options.limit,
        offset: options.offset,
      };

      const anonId = getOrCreateAnonymousId();
      const mergedConfig = {
        ...config,
        headers: {
          ...(config.headers || {}),
          ...(anonId ? { 'X-Anonymous-Id': anonId } : {}),
        },
      };

      const response = await apiClient.post('/schools/public-search', payload, mergedConfig);
      return response.data;
    } catch (error) {
      console.error('Error searching schools (public):', error);
      // Surface structured rate-limit detail if present
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        const message = detail.message || detail.detail || 'Failed to search schools';
        const err = new Error(message);
        err.code = detail.code;
        err.login_required = detail.login_required;
        err.retry_after_seconds = detail.retry_after_seconds;
        throw err;
      }
      throw new Error(detail || 'Failed to search schools');
    }
  },

  // Get curated list of STEM schools
  getBestForStem: async () => {
    try {
      const response = await apiClient.get('/schools/best-for-stem');
      return response.data;
    } catch (error) {
      console.error('Error loading STEM schools:', error);
      throw new Error(error.response?.data?.detail || 'Failed to load STEM schools');
    }
  },

  // Get curated list of best schools for athletes
  getBestForAthletes: async () => {
    try {
      const response = await apiClient.get('/schools/best-for-athletes');
      return response.data;
    } catch (error) {
      console.error('Error loading athlete schools:', error);
      throw new Error(error.response?.data?.detail || 'Failed to load athlete schools');
    }
  },

  getNearbyAthleticsSchools: async ({ latitude, longitude, radius_miles = 50, limit = 20 }) => {
    try {
      const payload = { latitude, longitude, radius_miles, limit };
      const response = await apiClient.post('/schools/nearby-athletics', payload);
      return response.data;
    } catch (error) {
      console.error('Error loading nearby athletics schools:', error);
      throw new Error(error.response?.data?.detail || 'Failed to load nearby athletics schools');
    }
  },

  getNearbyStemSchools: async ({ latitude, longitude, radius_miles = 50, limit = 10 }) => {
    try {
      const payload = { latitude, longitude, radius_miles, limit };
      const response = await apiClient.post('/schools/nearby-stem', payload);
      return response.data;
    } catch (error) {
      console.error('Error loading nearby STEM schools:', error);
      throw new Error(error.response?.data?.detail || 'Failed to load nearby STEM schools');
    }
  },

  // Get details for a specific school
  getSchoolDetails: async (schoolId, params = {}) => {
    try {
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
      );
      const response = await apiClient.get(`/schools/${schoolId}`, { params: filteredParams });
      return response.data;
    } catch (error) {
      console.error('Error getting school details:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get school details');
    }
  },

  // Fetch source metadata (og:image) for scholarship sources
  getSourceMetadata: async (urls = []) => {
    try {
      const response = await apiClient.post('/schools/source-metadata', { urls });
      return response.data;
    } catch (error) {
      console.error('Error fetching source metadata:', error);
      return { success: false, data: {} };
    }
  },

  // Get general statistics
  getStats: async () => {
    try {
      const response = await apiClient.get('/schools/');
      return response.data;
    } catch (error) {
      console.error('Error getting stats:', error);
      throw new Error(error.response?.data?.detail || 'Failed to get statistics');
    }
  },

  // Health check
  healthCheck: async () => {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw new Error('API health check failed');
    }
  },

  // App feedback (not school-specific)
  sendAppFeedback: async ({ rating, comments, userEmail, userName }) => {
    try {
      const response = await apiClient.post('/feedback/app', {
        rating,
        comments,
        userEmail,
        userName,
        recipient: 'harish.snaphomz@outlook.com',
      });
      return response.data;
    } catch (error) {
      // Gracefully fall back if the endpoint is not available by opening an email draft.
      const subject = encodeURIComponent('SnapGrad App Feedback');
      const body = encodeURIComponent(
        `User: ${userName || 'Unknown'}\nEmail: ${userEmail || 'Unknown'}\nRating: ${rating}/5\n\nFeedback:\n${comments || '(no comments)'}`
      );
      if (typeof window !== 'undefined') {
        window.location.href = `mailto:harish.snaphomz@outlook.com?subject=${subject}&body=${body}`;
      }
      // Treat fallback as success so the UI doesn't block the user.
      return { success: true, fallback: 'mailto' };
    }
  },
};

// Debug helper to check token status
export const debugAuth = () => {
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
  if (!clientId) {
    console.log('[Debug] REACT_APP_COGNITO_CLIENT_ID not set');
    return;
  }
  
  const lastAuthResultKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthResult`;
  console.log('[Debug] Looking for key:', lastAuthResultKey);
  
  const authResultStr = localStorage.getItem(lastAuthResultKey);
  if (!authResultStr) {
    console.log('[Debug] No auth result found in localStorage');
    console.log('[Debug] Available localStorage keys:', Object.keys(localStorage).filter(k => k.includes('Cognito')));
    return;
  }
  
  const authResult = JSON.parse(authResultStr);
  console.log('[Debug] Auth result found:', {
    hasAccessToken: !!authResult.accessToken,
    hasIdToken: !!authResult.idToken,
    hasRefreshToken: !!authResult.refreshToken,
    accessTokenLength: authResult.accessToken?.length,
    accessTokenPreview: authResult.accessToken?.substring(0, 20) + '...',
  });
};

export default apiClient;
